import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { CATEGORIES } from "../lib/constants";
import { Instagram, Facebook, Twitter, MapPin, Mail, Phone, Send, Truck, ShieldCheck, Repeat, CreditCard } from "lucide-react";
import { toast } from "sonner";

export default function Footer() {
  const { settings } = useApp();
  const [email, setEmail] = useState("");
  const subscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    toast.success("Subscribed! Watch your inbox for exclusive drops & offers 🎉");
    setEmail("");
  };

  return (
    <footer className="mt-20">
      {/* newsletter band */}
      <div className="relative overflow-hidden bg-gradient-to-r from-[#E63946] via-[#c81e2c] to-[#1D3557]">
        <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-20 left-1/4 w-72 h-72 rounded-full bg-[#D4AF37]/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-white flex items-center gap-2">
              <Mail className="text-[#D4AF37]" /> Join the Bull Family
            </h3>
            <p className="text-white/85 mt-2 text-sm sm:text-base">
              Get <span className="font-bold text-[#D4AF37]">10% off</span> your first order plus early access to new drops across India & the GCC.
            </p>
          </div>
          <form onSubmit={subscribe} className="flex gap-2 w-full max-w-md md:ml-auto">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              data-testid="newsletter-email"
              className="flex-1 h-12 px-4 rounded-full bg-white/95 text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
            />
            <button type="submit" data-testid="newsletter-subscribe" className="h-12 px-6 rounded-full bg-[#1D3557] hover:bg-[#0f1f38] text-white font-semibold flex items-center gap-2 shadow-lg transition-colors">
              Subscribe <Send size={16} />
            </button>
          </form>
        </div>
      </div>

      {/* trust strip */}
      <div className="bg-[#162943] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-5 text-white">
          {[
            { icon: Truck, t: "Free Shipping", s: "Orders over ₹1999" },
            { icon: ShieldCheck, t: "Shrink-Proof", s: "Tiruppur quality" },
            { icon: Repeat, t: "Easy Returns", s: "7-day window" },
            { icon: CreditCard, t: "Secure Payments", s: "Cards • UPI • Wallet" },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#E63946]/20 flex items-center justify-center text-[#E63946] shrink-0">
                <f.icon size={18} />
              </div>
              <div>
                <div className="font-semibold text-sm">{f.t}</div>
                <div className="text-xs text-slate-400">{f.s}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* main footer */}
      <div className="relative overflow-hidden bg-[#0c1a2f] text-slate-300">
        {/* layered radial glows for depth */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_circle_at_8%_-10%,rgba(230,57,70,0.22),transparent_55%),radial-gradient(760px_circle_at_95%_115%,rgba(212,175,55,0.16),transparent_55%)]" />
        {/* subtle dot-grid texture */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.5] [background-image:radial-gradient(rgba(255,255,255,0.05)_1px,transparent_1px)] [background-size:22px_22px]" />
        {/* gold + crimson accent top line */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#E63946] via-[#D4AF37] to-[#E63946]" />
        {/* large faded brand watermark */}
        <img src="/brand/bull-mark.png" alt="" aria-hidden="true" className="pointer-events-none select-none absolute -right-20 top-1/2 -translate-y-1/2 w-[460px] max-w-none opacity-[0.045]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2.5 mb-2">
              <img src="/brand/bull-mark.png" alt="Bull Creations" className="h-12 w-auto drop-shadow-[0_2px_8px_rgba(230,57,70,0.4)]" />
              <div className="font-display font-extrabold text-2xl sm:text-3xl text-white tracking-tight">
                BULL<span className="text-[#E63946]">CREATIONS</span>
              </div>
            </div>
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37] mb-3">
              Unleash Your Everyday
            </div>
            <p className="text-sm leading-relaxed text-slate-300/90 mb-4">
              Precision cotton & knitwear crafted in Tiruppur, India's knitwear capital. Vibrant everyday
              essentials — tees, shirts, joggers & more — shipped across India & the GCC.
            </p>
            <div className="inline-flex items-center gap-2 text-sm text-slate-300 bg-white/5 border border-white/10 rounded-full px-3 py-1.5">
              <MapPin size={16} className="text-[#D4AF37]" /> Tiruppur, Tamil Nadu, India
            </div>
          </div>

          <div>
            <h4 className="font-display font-bold text-white mb-4 inline-block after:block after:mt-1.5 after:h-0.5 after:w-8 after:rounded-full after:bg-[#D4AF37]">Shop</h4>
            <ul className="space-y-2.5 text-sm">
              {CATEGORIES.map((c) => (
                <li key={c.key}>
                  <Link to={`/shop?category=${c.key}`} className="hover:text-[#E63946] transition-colors">
                    {c.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-bold text-white mb-4 inline-block after:block after:mt-1.5 after:h-0.5 after:w-8 after:rounded-full after:bg-[#D4AF37]">Company</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/shop" className="hover:text-[#E63946]">All Products</Link></li>
              <li><Link to="/wallet" className="hover:text-[#E63946]">Bull Family Rewards</Link></li>
              <li><span className="opacity-70">About Tiruppur</span></li>
              <li><Link to="/returns" className="hover:text-[#E63946]">Returns & Exchange</Link></li>
              <li><Link to="/size-guide" className="hover:text-[#E63946]">Size Guide</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display font-bold text-white mb-4 inline-block after:block after:mt-1.5 after:h-0.5 after:w-8 after:rounded-full after:bg-[#D4AF37]">We Ship To</h4>
            <ul className="space-y-2.5 text-sm text-slate-400">
              <li>🇮🇳 India (INR ₹)</li>
              <li>🇦🇪 UAE &nbsp; 🇸🇦 KSA &nbsp; 🇶🇦 Qatar</li>
              <li>🇴🇲 Oman &nbsp; 🇰🇼 Kuwait &nbsp; 🇧🇭 Bahrain</li>
            </ul>
            <div className="mt-5 space-y-1.5 text-sm text-slate-400">
              {settings?.support_email && <a href={`mailto:${settings.support_email}`} className="flex items-center gap-2 hover:text-[#E63946]"><Mail size={15} className="text-[#D4AF37]" /> {settings.support_email}</a>}
              {settings?.support_phone && <a href={`tel:${settings.support_phone}`} className="flex items-center gap-2 hover:text-[#E63946]"><Phone size={15} className="text-[#D4AF37]" /> {settings.support_phone}</a>}
            </div>
            <div className="flex gap-3 mt-5">
              {[["instagram", Instagram], ["facebook", Facebook], ["twitter", Twitter]].map(([key, Icon]) => {
                const url = settings?.social?.[key];
                return (
                  <a key={key} href={url || "#"} target={url ? "_blank" : undefined} rel="noreferrer"
                    className={`w-9 h-9 rounded-full bg-white/10 flex items-center justify-center transition-colors ${url ? "hover:bg-[#E63946]" : "opacity-50 cursor-default"}`}>
                    <Icon size={17} className="text-white" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* bottom bar */}
      <div className="bg-gradient-to-r from-[#0c1830] via-[#0f1f38] to-[#0c1830] border-t border-[#D4AF37]/20 py-5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <span className="flex items-center gap-2">
            <span className="font-display font-bold text-white">BULL<span className="text-[#E63946]">CREATIONS</span></span>
            <span className="text-slate-500">© {new Date().getFullYear()} · Registered in Tiruppur, India · All rights reserved.</span>
          </span>
          <div className="flex items-center gap-2">
            {["VISA", "Mastercard", "RuPay", "UPI", "AmEx"].map((p) => (
              <span key={p} className="px-2 py-1 rounded bg-white/10 text-[10px] font-bold text-slate-200">{p}</span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
